import { useState, useCallback } from 'react';
import { API_URL } from '../constants/api';
import { trackEvent, Events } from '../lib/posthog';

// Resize image to max 1280x720 via canvas, return base64 (no prefix)
function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX_W = 1280;
      const MAX_H = 720;
      let w = img.width;
      let h = img.height;
      if (w > MAX_W || h > MAX_H) {
        const scale = Math.min(MAX_W / w, MAX_H / h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      const base64 = dataUrl.split(',')[1];
      resolve({ base64, dataUrl });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

export function useAnalysis({
  deviceId,
  credits,
  isAdmin,
  setShowPricingModal,
  deductCredit,
  saveSwingToHistory,
  swingHistory,
}) {
  const [selectedImages, setSelectedImages] = useState([]); // { file, preview, base64 }
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState('');
  const [selectedClub, setSelectedClub] = useState('7-iron');
  const [currentSwingId, setCurrentSwingId] = useState(null);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [playerContext, setPlayerContext] = useState('');

  const handleImageSelect = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Filter to image types only
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      alert('Please select image files (JPEG, PNG, HEIC)');
      return;
    }

    // Limit to 7 total
    const remaining = 7 - selectedImages.length;
    const toAdd = imageFiles.slice(0, remaining);

    try {
      const processed = await Promise.all(
        toAdd.map(async (file) => {
          const { base64, dataUrl } = await resizeImage(file);
          return { file, preview: dataUrl, base64 };
        })
      );
      setSelectedImages(prev => [...prev, ...processed]);
    } catch (err) {
      console.error('Image processing error:', err);
      alert('Failed to process one or more images. Please try again.');
    }

    // Reset input so same file can be re-selected
    event.target.value = '';
  }, [selectedImages.length]);

  const removeImage = useCallback((index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const analyzeSwing = useCallback(async () => {
    if (credits === 0 && !isAdmin) {
      setShowPricingModal(true);
      return;
    }
    if (selectedImages.length === 0) return;

    setIsAnalyzing(true);
    setAnalysisProgress('Sending images to AI...');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    trackEvent(Events.ANALYSIS_STARTED, {
      club: selectedClub,
      imageCount: selectedImages.length,
    });

    try {
      const requestBody = {
        images: selectedImages.map(img => img.base64),
        club: selectedClub,
        deviceId,
        playerContext: playerContext.trim() || null,
      };

      setAnalysisProgress('Analyzing your swing...');

      let response;
      try {
        response = await fetch(`${API_URL}/api/analyze-swing`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(requestBody),
        });
      } catch (fetchError) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (response.status === 502 || response.status === 503 || response.status === 504) {
          throw new Error('Server is temporarily unavailable. Please try again in a moment.');
        }
        throw new Error(`Server error (${response.status}). Please try again.`);
      }

      if (!response.ok) {
        const error = await response.json();
        if (error.rateLimitExceeded) throw new Error(error.error);
        if (response.status === 429) throw new Error('Too many requests. Please wait a moment and try again.');
        if (response.status === 503) throw new Error('AI is temporarily busy. Please try again in a moment.');
        throw new Error(error.message || error.error || 'Analysis failed. Please try again.');
      }

      const result = await response.json();

      // Attach uploaded image previews for display
      result.uploadedImages = selectedImages.map(img => img.preview);

      setAnalysisResult(result);

      if (result.swingId !== null && result.swingId !== undefined) {
        setCurrentSwingId(result.swingId);
      }

      deductCredit();
      setIsAnalyzing(false);

      trackEvent(Events.ANALYSIS_COMPLETED, {
        club: selectedClub,
        swingScore: result.swingScore?.overall,
        imageCount: selectedImages.length,
      });

      if (saveSwingToHistory) {
        saveSwingToHistory(result, null, selectedClub);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setIsAnalyzing(false);
      trackEvent(Events.ANALYSIS_FAILED, { club: selectedClub, error: error.message });
      alert(error.message || 'Analysis failed. Please try again.');
    }
  }, [
    selectedImages, credits, isAdmin, setShowPricingModal, selectedClub,
    deviceId, playerContext, deductCredit, saveSwingToHistory,
  ]);

  const resetAnalysis = useCallback(() => {
    setSelectedImages([]);
    setAnalysisResult(null);
    setIsAnalyzing(false);
    setShowFullAnalysis(false);
    setCurrentSwingId(null);
    setPlayerContext('');
  }, []);

  return {
    selectedImages,
    isAnalyzing,
    analysisResult,
    setAnalysisResult,
    analysisProgress,
    selectedClub,
    setSelectedClub,
    currentSwingId,
    setCurrentSwingId,
    showFullAnalysis,
    setShowFullAnalysis,
    playerContext,
    setPlayerContext,
    handleImageSelect,
    removeImage,
    analyzeSwing,
    resetAnalysis,
  };
}
